import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { SlashSquare } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';

interface DisabledProtocolsTriggerProps {
  tooltipText: string;
  ariaLabel: string;
}

/**
 * Native `disabled` bundles three effects and we only want two of them: it dims
 * the control and blocks clicks, but it also drops the button from the tab order
 * and kills its pointer events, so the tooltip explaining the dimming can be
 * neither hovered nor focused.
 *
 * The wrapper is what the assistive tree sees, then — it carries the role, name
 * and disabled state, and the button underneath is hidden from it, keeping the
 * design system's `disabled:` styling without announcing the control twice.
 * Making the button itself `aria-disabled` would be the tidier shape and would
 * remove this wrapper entirely, but the design system's disabled treatment is
 * keyed on the native attribute, so that is a change in the package.
 */
export const DisabledProtocolsTrigger = ({
  tooltipText,
  ariaLabel,
}: DisabledProtocolsTriggerProps) => (
  <div className="shrink-0">
    <Tooltip delayDuration={400}>
      <TooltipTrigger asChild>
        <span
          role="button"
          aria-disabled="true"
          aria-label={ariaLabel}
          className="inline-flex"
          tabIndex={0}
        >
          <Button type="button" variant="ghost" size="sm" iconOnly disabled aria-hidden>
            <SlashSquare size={16} />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  </div>
);

export const ProtocolsTrigger = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof Button> & {
    isOpen: boolean;
    ariaLabel: string;
  }
>(({ isOpen, ariaLabel, className, ...props }, ref) => (
  <Button
    {...props}
    ref={ref}
    type="button"
    variant="ghost"
    size="sm"
    iconOnly
    data-testid="evidence-shortcuts-trigger"
    aria-label={ariaLabel}
    aria-expanded={isOpen}
    className={cn(isOpen && 'bg-surface-pressed text-text-secondary', className)}
  >
    <SlashSquare size={16} />
  </Button>
));

ProtocolsTrigger.displayName = 'ProtocolsTrigger';
